import { Button } from "@bigbinary/neeto-atoms";
import classnames from "classnames";
import { X } from "lucide-react";
import useHotkeys from "neetohotkeys";
import { manager } from "neetoui/managers";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import HotKeyList from "./HotKeyList";
import { getGlobalShortcuts } from "./utils";

import useKeyboardShortcutsPaneState from "../hooks/useKeyboardShortcutsPaneState";

const KeyboardShortcutsPane = ({ productShortcuts = {} }) => {
  const [isOpen, setIsOpen] = useKeyboardShortcutsPaneState();
  const hasOverlays = manager.hasOverlays();
  // eslint-disable-next-line @bigbinary/neeto/no-dangling-constants
  const GLOBAL_SHORTCUTS = getGlobalShortcuts();
  const { t } = useTranslation();

  const shortcuts =
    GLOBAL_SHORTCUTS[t("neetoMolecules.keyboardShortcuts.global.categoryName")];

  useHotkeys(
    shortcuts.openKeyboardShortcutsPane.sequence,
    () => setIsOpen(prevIsOpen => !prevIsOpen),
    { mode: "global" }
  );

  useHotkeys(shortcuts.close.sequence, () => setIsOpen(false), {
    mode: "global",
    enabled: isOpen,
  });

  return (
    <div
      data-testid="keyboard-shortcuts-pane"
      style={{ zIndex: "var(--nv-pane-z-index, 100000)" }}
      className={classnames(
        "border-border bg-background transition-width ms-auto h-dvh shrink-0 grow-0 overflow-hidden border-l duration-300 ease-in-out rtl:border-r rtl:border-l-0",
        {
          "w-80": isOpen,
          "w-0": !isOpen,
          absolute: hasOverlays,
          "end-0": hasOverlays,
        }
      )}
    >
      <div className="h-full w-80 overflow-y-auto">
        <div className="border-border my-2 flex items-center justify-between border-b px-4 pb-2">
          <h4
            className="text-foreground text-base font-semibold"
            data-testid="keyboard-shortcuts-pane-title"
          >
            {t("neetoMolecules.keyboardShortcuts.title")}
          </h4>
          <Button
            data-testid="keyboard-shortcuts-pane-close-button"
            icon={X}
            size="icon"
            variant="ghost"
            onClick={() => setIsOpen(false)}
          />
        </div>
        <HotKeyList hotkeys={GLOBAL_SHORTCUTS} />
        <HotKeyList hotkeys={productShortcuts} />
      </div>
    </div>
  );
};

KeyboardShortcutsPane.propTypes = {
  /**
   * Used to pass in the product specific keyboard shortcuts.
   */
  productShortcuts: PropTypes.object,
};

export default KeyboardShortcutsPane;
